import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Modal, InputGroup, ListGroup, Spinner } from 'react-bootstrap';
import { fetchImages, uploadImage, updateImage, deleteImage, fetchUsers, fetchImageTypes, fetchUserByID } from '../../services/imageService';
import { useAuthContext } from '../../contexts/AuthContext';
import dateService from '../../services/dateService';
import DiagnosisManagement from './DiagnosisManagement';
import ImageViewer from '../../components/ImageViewer';

const ImageManagement = () => {
  const { user } = useAuthContext();
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedImageType, setSelectedImageType] = useState('');
  const [imageTypes, setImageTypes] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [isLoading, setIsLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({ radiologists: [], doctors: [], patients: [] });
  const [selectedUsers, setSelectedUsers] = useState({ radiologist: null, doctor: null, patient: null });
  const [searchInputs, setSearchInputs] = useState({ radiologist: '', doctor: '', patient: '' });
  const [currentImage, setCurrentImage] = useState({
    imageName: '', description: '', imageTypeID: '', cost: 0,
    radiologistId: '', doctorId: '', patientId: '', file: null, createdBy: user?.id
  });
  const [showAllImages, setShowAllImages] = useState(false);

  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  const [showImageViewer, setShowImageViewer] = useState(false);
const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  useEffect(() => { loadInitialData(); }, [showAllImages]);
  useEffect(() => { filterImages(); }, [images, search, selectedImageType, userDetails,showAllImages]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadImages(), loadImageTypes()]);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadImages = async () => {
  try {
    const data = await fetchImages();
       
    // Filter images based on user role and showAllImages setting
    let filteredData = data;
    if (!showAllImages && user) {
      switch (user.role?.toLowerCase()) {
        case 'radiologist':
          filteredData = data.filter(img => img.radiologistID === user.id);
          break;
        case 'doctor':
          filteredData = data.filter(img => img.doctorID === user.id);
          break;
        case 'patient':
          filteredData = data.filter(img => img.patientID === user.id);
          break;
      }
    }
    
    setImages(filteredData);

        // Collect all unique user IDs from the images
        const userIds = new Set();
        data.forEach(image => {
          if (image.patientID) userIds.add(image.patientID); // Note: using patientID not patientId
          if (image.doctorID) userIds.add(image.doctorID);   // Note: using doctorID not doctorId
          if (image.radiologistID) userIds.add(image.radiologistID); // Note: using radiologistID not radiologistId
          if (image.createdBy) userIds.add(image.createdBy);
        });

              // Fetch user details for each ID
      const userDetailsPromises = Array.from(userIds).map(id => fetchUserByID(id));
      const userDetailsResponses = await Promise.all(userDetailsPromises);
  
      // Create user details map
      const userDetailsMap = {};
      Array.from(userIds).forEach((id, index) => {
        userDetailsMap[id] = userDetailsResponses[index];
      });
  
     
      setUserDetails(userDetailsMap);
    
    // Rest of your existing loadImages code for fetching user details...
  } catch (error) {
    console.error('Failed to load images:', error);
  }
};

const loadImageTypes = async () => {
  try {
    const types = await fetchImageTypes();
    setImageTypes(types);
  } catch (error) {
    console.error('Failed to load image types:', error);
  }
};

const filterImages = () => {
  if (!images) return;
  let filtered = [...images];
  
  // First apply the role-based filtering if showAllImages is false
  if (!showAllImages && user) {
    switch (user.role?.toLowerCase()) {
      case 'radiologist':
        filtered = filtered.filter(img => img.radiologistID === user.id);
        break;
      case 'doctor':
        filtered = filtered.filter(img => img.doctorID === user.id);
        break;
      case 'patient':
        filtered = filtered.filter(img => img.patientID === user.id);
        break;
    }
  }
    
  if (search) {
    const searchLower = search.toLowerCase();
    filtered = filtered.filter(image => {
      const imageMatch = 
        (image.imageName?.toLowerCase().includes(searchLower)) || 
        (image.description?.toLowerCase().includes(searchLower));

      const doctorDetails = userDetails[image.doctorID];
      const patientDetails = userDetails[image.patientID];
      const radiologistDetails = userDetails[image.radiologistID];

      const userMatch = [doctorDetails, patientDetails, radiologistDetails].some(user => {
        if (!user) return false;
        return (
          user.username?.toLowerCase().includes(searchLower) ||
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.userCode?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
        );
      });

      return imageMatch || userMatch;
    });
  }
  if (selectedImageType) {
    filtered = filtered.filter(image => 
      image.imageTypeID.toString() === selectedImageType.toString()
    );
  }
  
    setFilteredImages(filtered);
  };

 

  const formatUserDisplay = (userId) => {
    if (!userId || !userDetails[userId]) return 'N/A';
    const user = userDetails[userId];
    
    // If both firstName and lastName are available, show them with username
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName} (${user.username})`;
    }
    
    // Otherwise just show username
    return user.username;
  };

  const handleSearch = async (type, query) => {
    setSearchInputs(prev => ({ ...prev, [type]: query }));
    if (query.length < 2) {
      setSuggestions(prev => ({ ...prev, [type + 's']: [] }));
      return;
    }
    try {
      const users = await fetchUsers(type, query);
      setSuggestions(prev => ({ ...prev, [type + 's']: users }));
    } catch (error) {
      console.error(`Failed to fetch ${type} suggestions:`, error);
      setSuggestions(prev => ({ ...prev, [type + 's']: [] }));
    }
  };

  const handleNoteClick = (image) => {
    setSelectedImageId(image.imageID);
    setSelectedPatientId(image.patientID);
    setShowDiagnosisModal(true);
  };

  const handleUserSelect = (type, user) => {
    setSelectedUsers(prev => ({ ...prev, [type]: user }));
    const displayText = user.firstName && user.lastName ? 
      `${user.username} (${user.firstName} ${user.lastName})` : 
      user.username;
    setSearchInputs(prev => ({ ...prev, [type]: displayText }));
    
    // Use the correct ID casing
    const idKey = `${type}ID`;  // This will create 'patientID', 'doctorID', etc.
    setCurrentImage(prev => ({ ...prev, [idKey]: user.id }));
    
    setSuggestions(prev => ({ ...prev, [type + 's']: [] }));
  };
  const clearUserSelection = (type) => {
    setSelectedUsers(prev => ({ ...prev, [type]: null }));
    setSearchInputs(prev => ({ ...prev, [type]: '' }));
    const idKey = `${type}ID`;  // Use correct casing
    setCurrentImage(prev => ({ ...prev, [idKey]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentImage.patientID && !currentImage.patientId) {
      alert('Please select a patient');
      return;
    }
  
    const formData = new FormData();
    Object.keys(currentImage).forEach(key => {
      // Skip the file field in edit mode if no new file is selected
      if (key === 'file' && modalMode === 'edit' && !currentImage[key]) {
        return;
      }
      
      if (currentImage[key] !== null && currentImage[key] !== undefined) {
        // Make sure we're using the correct casing for IDs
        if (key === 'patientId') formData.append('patientID', currentImage[key]);
        else if (key === 'doctorId') formData.append('doctorID', currentImage[key]);
        else if (key === 'radiologistId') formData.append('radiologistID', currentImage[key]);
        else formData.append(key, currentImage[key]);
      }
    });
  
    try {
      if (modalMode === 'add') {
        await uploadImage(formData);
      } else {
        // In edit mode, append a placeholder if no new file is selected
        // This tells the backend we're not updating the file
        if (!currentImage.file) {
          formData.append('file', new Blob([''], { type: 'application/octet-stream' }));
        }
        await updateImage(currentImage.imageID, formData);
      }
      setShowModal(false);
      await loadImages();
      resetForm();
    } catch (error) {
      console.error('Failed to save image:', error);
      alert('Failed to save image. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteImage(id);
        await loadImages();
      } catch (error) {
        console.error('Failed to delete image:', error);
        alert('Failed to delete image. Please try again.');
      }
    }
  };

  const handleEdit = (image) => {
    const userTypes = ['radiologist', 'doctor', 'patient'];
    setCurrentImage({ ...image, file: null });
    userTypes.forEach(type => {
      const userId = image[`${type}ID`]; // Note: using ID not Id
      const userDetail = userDetails[userId];
      if (userDetail) {
        setSelectedUsers(prev => ({ ...prev, [type]: userDetail }));
        const displayText = userDetail.firstName && userDetail.lastName 
          ? `${userDetail.username} (${userDetail.firstName} ${userDetail.lastName})`
          : userDetail.username;
        setSearchInputs(prev => ({ ...prev, [type]: displayText }));
      }
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const resetForm = () => {
    setCurrentImage({
      imageName: '', description: '', imageTypeID: '', cost: 0,
      radiologistId: '', doctorId: '', patientId: '', file: null,
      createdBy: user?.id
    });
    setSelectedUsers({ radiologist: null, doctor: null, patient: null });
    setSearchInputs({ radiologist: '', doctor: '', patient: '' });
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col><h2>Image Management</h2></Col>
      </Row>
      <Row className="mb-3">
  <Col md={4}>
    <InputGroup>
      <Form.Control
        placeholder="Search in name, description, or users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Button variant="outline-secondary" onClick={() => setSearch('')}>Clear</Button>
    </InputGroup>
  </Col>
  <Col md={3}>
    <Form.Select
      value={selectedImageType}
      onChange={(e) => setSelectedImageType(e.target.value)}
    >
      <option value="">All Image Types</option>
      {imageTypes.map((type) => (
        <option key={type.id} value={type.id}>{type.name}</option>
      ))}
    </Form.Select>
  </Col>
  <Col md={2}>
    <Form.Select
      value={showAllImages}
      onChange={(e) => setShowAllImages(e.target.value === 'true')}
    >
      <option value="false">My Images</option>
      <option value="true">All Images</option>
    </Form.Select>
  </Col>
  <Col md={3} className="text-end">  {user?.role?.toLowerCase() !== 'patient' && (
    <Button onClick={() => { resetForm(); setModalMode('add'); setShowModal(true); }}>
      Add New Image
    </Button>)}
  </Col>
</Row>
      <Card>
        <Card.Body>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Description</th>
                <th>Type</th>
                <th>Cost</th>
                <th>Doctor</th>
                <th>Patient</th>
                <th>Radiologist</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredImages.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No images found</td></tr>
              ) : (
                filteredImages.map(image => (
                  <tr key={image.imageID}>
              <td>
  <img 
    src={image.imageURL} 
    alt={image.imageName}
    style={{ 
      width: '64px', 
      height: '64px', 
      objectFit: 'cover',
      cursor: 'pointer' 
    }}
    onClick={() => {
      setSelectedImageUrl(image.imageURL);
      setShowImageViewer(true);
    }}
    onError={(e) => { 
      e.target.onerror = null; 
      e.target.src = 'placeholder-image-url'; 
    }} 
  />
</td>
                    <td>{image.imageName}</td>
                    <td>{image.description}</td>
                    <td>{image.imageTypeName}</td>
                    <td>${parseFloat(image.cost).toFixed(2)}</td>
                    <td>{formatUserDisplay(image.doctorID)}</td>
                    <td>{formatUserDisplay(image.patientID)}</td>
                    <td>{formatUserDisplay(image.radiologistID)}</td>
                    <td>{dateService.formatDate(image.createdAt)}</td>

                    <td>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        className="me-2" 
                        onClick={() => handleEdit(image)}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleDelete(image.imageID)}
                      >
                        Delete
                      </Button>

                      {(user?.role?.toLowerCase() === 'radiologist' || 
      user?.role?.toLowerCase() === 'doctor') && (
      <Button
        variant="info"
        size="sm"
        className="me-2"
        onClick={() => handleNoteClick(image)}
      >
        {user.role.toLowerCase() === 'doctor' ? 'Add Diagnosis' : 'Add Comment'}
      </Button>
    )}

</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New Image' : 'Edit Image'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={currentImage.imageName}
                    onChange={(e) => setCurrentImage(prev => ({
                      ...prev, imageName: e.target.value
                    }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={currentImage.imageTypeID}
                    onChange={(e) => setCurrentImage(prev => ({
                      ...prev, imageTypeID: e.target.value
                    }))}
                    required
                  >
                    <option value="">Select Type</option>
                    {imageTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={currentImage.description || ''}
                onChange={(e) => setCurrentImage(prev => ({
                  ...prev, description: e.target.value
                }))}
              />
            </Form.Group>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Cost</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentImage.cost}
                    onChange={(e) => setCurrentImage(prev => ({
                      ...prev, cost: parseFloat(e.target.value) || 0
                    }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Image File</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => setCurrentImage(prev => ({
                      ...prev, file: e.target.files[0]
                    }))}
                    accept="image/*"
                    required={modalMode === 'add'}
                  />
                </Form.Group>
              </Col></Row>
            {['radiologist', 'doctor', 'patient'].map(type => (
              <Form.Group key={type} className="mb-3">
                <Form.Label>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                  {type === 'patient' && ' *'}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={searchInputs[type]}
                    onChange={(e) => handleSearch(type, e.target.value)}
                    placeholder={`Search ${type}...`}
                    required={type === 'patient'}
                  />
                  {selectedUsers[type] && (
                    <Button 
                      variant="outline-secondary"
                      onClick={() => clearUserSelection(type)}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
                {suggestions[type + 's'].length > 0 && (
                  <ListGroup className="mt-1">
                    {suggestions[type + 's'].map(user => (
                      <ListGroup.Item
                        key={user.id}
                        action
                        onClick={() => handleUserSelect(type, user)}
                      >
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName} (${user.username})`
                          : user.username}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Form.Group>
            ))}
            <div className="text-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {modalMode === 'add' ? 'Add Image' : 'Update Image'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <DiagnosisManagement
        show={showDiagnosisModal}
        onHide={() => {
          setShowDiagnosisModal(false);
          setSelectedImageId(null);
          setSelectedPatientId(null);
        }}
        imageId={selectedImageId}
        userRole={user?.role}
       
        patientId={selectedPatientId}  
      />

<ImageViewer
  show={showImageViewer}
  onHide={() => setShowImageViewer(false)}
  imageUrl={selectedImageUrl}
/>
    </Container>
  );
};

export default ImageManagement;